import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, MapPin, Phone, ShieldCheck, ArrowRight } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { EMERGENCY, PATCHES } from '@/data/guides';

/* /guides — the destination of the Coming Home card QR. Kept minimal on
 * purpose: emergency-first, then the live directory of local ranger and
 * guide partnerships, patch by patch. */

const HERO =
  'https://images.unsplash.com/photo-1687876790754-2a92a8f0686d?fm=jpg&q=70&w=2000&auto=format&fit=crop';

// Fire an analytics ping the first time this browser lands on the page
// with `?src=card` (i.e. from a Coming Home card QR). One ping per day
// per browser.
function useCardScanTracking() {
  const [params] = useSearchParams();
  useEffect(() => {
    const src = params.get('src');
    if (!src) return;
    const key = `qr-scan-fired:${new Date().toISOString().slice(0, 10)}:${src}`;
    if (typeof window === 'undefined' || window.localStorage.getItem(key)) return;
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) return;
    // Fire-and-forget; analytics must never break the page.
    fetch(`${url}/api/qr/scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: src }),
      keepalive: true,
    })
      .then(() => window.localStorage.setItem(key, '1'))
      .catch(() => { /* silent */ });
  }, [params]);
}

export default function GuidesPage() {
  useCardScanTracking();
  return (
    <>
      <Navigation />
      <main
        data-testid="guides-page"
        className="bg-[var(--brand-parchment)] text-[var(--brand-forest)] pb-24"
      >
        {/* Hero */}
        <div className="relative h-[46svh] min-h-[340px] w-full overflow-hidden">
          <img src={HERO} alt="Warm sunrise over a Highland loch" className="absolute inset-0 w-full h-full object-cover" />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(4,20,10,0.60) 0%, rgba(4,20,10,0.40) 40%, rgba(4,20,10,0.88) 100%)',
            }}
          />
          <div className="absolute inset-x-0 bottom-0 max-w-4xl mx-auto px-6 lg:px-10 pb-10 text-[var(--brand-parchment)]">
            <span className="font-eyebrow !text-[var(--brand-gold)]">Local Partners &amp; Emergency Contacts</span>
            <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-[1.02] [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]">
              Come home to <span className="italic text-[var(--brand-gold)]">tell the tale.</span>
            </h1>
          </div>
        </div>

        {/* Emergency band — top priority, red accent on gold */}
        <section
          data-testid="guides-emergency"
          className="max-w-4xl mx-auto px-6 lg:px-10 mt-12"
        >
          <div className="border-l-4 border-[#B84A3E] bg-[#B84A3E]/8 px-6 py-6">
            <div className="flex items-start gap-3">
              <AlertTriangle size={22} className="text-[#B84A3E] mt-1 shrink-0" />
              <div>
                <h2 className="font-display text-2xl text-[var(--brand-forest-deep)]">In a genuine emergency</h2>
                <p className="mt-1 text-sm text-[var(--brand-ink)]">
                  Ring for help first. Read the rest of this page afterwards.
                </p>
              </div>
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-4">
              {EMERGENCY.map((e) => (
                <div
                  key={e.label}
                  data-testid={`emergency-${e.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="bg-[var(--brand-parchment)] border border-[var(--brand-olive)]/25 px-4 py-3"
                >
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#B84A3E]">
                    <Phone size={12} /> {e.label}
                  </div>
                  <p className="mt-1 text-sm font-semibold text-[var(--brand-forest-deep)]">{e.detail}</p>
                </div>
              ))}
            </div>
            <Link
              to="/safety"
              data-testid="guides-safety-link"
              className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--brand-forest-deep)] link-expand"
            >
              <ShieldCheck size={14} />
              Full Safety &amp; Emergency guide &mdash; contacts, location tips
              <ArrowRight
                size={14}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </section>

        {/* Intro */}
        <section className="max-w-3xl mx-auto px-6 lg:px-10 mt-16">
          <h2 className="font-display text-3xl sm:text-4xl text-[var(--brand-forest)]">Rangers &amp; mountain guides.</h2>
          <p className="mt-4 text-[var(--brand-muted)] leading-relaxed">
            The best local knowledge is always from a person, not a page. Below
            is our live directory of the ranger services and mountain-guide
            partnerships we&rsquo;re building patch by patch. If you&rsquo;re heading
            somewhere new, ring the local ranger. If you&rsquo;re heading somewhere
            serious, hire a guide. It&rsquo;s cheap insurance and a shortcut to the
            best advice on the ground.
          </p>
          <p className="mt-4 text-[var(--brand-muted)] leading-relaxed">
            This is a young list. It will fill out as partnerships confirm.
            Nothing on this page is listed without the partner&rsquo;s explicit
            permission.
          </p>
        </section>

        {/* Patches */}
        <section
          data-testid="guides-patches"
          className="max-w-4xl mx-auto px-6 lg:px-10 mt-12 space-y-8"
        >
          {PATCHES.map((patch) => (
            <div
              key={patch.id}
              data-testid={`patch-${patch.id}`}
              className="border-t border-[var(--brand-olive)]/30 pt-6"
            >
              <div className="flex flex-wrap items-center gap-3">
                <MapPin size={18} className="text-[var(--brand-gold)]" />
                <h3 className="font-display text-2xl text-[var(--brand-forest-deep)]">
                  {patch.title}
                </h3>
                {patch.status === 'building' ? (
                  <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--brand-forest)] bg-[var(--brand-gold)]/15 border border-[var(--brand-gold-soft)] px-3 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-gold)] animate-pulse" />
                    Building the list
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--brand-parchment)] bg-[var(--brand-forest)] px-3 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-gold)]" />
                    Active
                  </span>
                )}
              </div>
              <p
                className="mt-3 text-sm text-[var(--brand-muted)] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: patch.note }}
              />
              {patch.partners.length > 0 && (
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {patch.partners.map((p) => (
                    <div key={p.name} className="bg-[var(--brand-forest)]/5 border border-[var(--brand-olive)]/25 px-4 py-3">
                      <div className="font-display text-base text-[var(--brand-forest-deep)]">{p.name}</div>
                      <div className="text-xs uppercase tracking-[0.18em] text-[var(--brand-muted)] mt-0.5">{p.credentials}</div>
                      {p.url && (
                        <a href={p.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-semibold text-[var(--brand-forest)] link-expand">
                          {p.url.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </section>

        {/* Partner-us CTA */}
        <section className="max-w-4xl mx-auto px-6 lg:px-10 mt-20">
          <div className="bg-[var(--brand-forest)] text-[var(--brand-parchment)] p-10 relative overflow-hidden">
            <div className="grain absolute inset-0 opacity-30" />
            <div className="relative">
              <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--brand-gold)]">
                Ranger service, mountain guide, or park authority?
              </span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl">Join the Coming Home card.</h2>
              <p className="mt-4 max-w-xl text-[var(--brand-parchment)]/90 leading-relaxed">
                We&rsquo;d be glad to list your patch. No commission, no revenue
                share, no paperwork &mdash; just your public contact details on
                the card that ships with every order.
              </p>
              <Link
                to="/#contact"
                data-testid="guides-partner-cta"
                className="btn-gold mt-6 inline-flex px-6 py-3 text-sm font-semibold tracking-wide items-center gap-2 rounded-none"
              >
                Get in touch
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
